/**
 * VoorleesTekst: tekst per zin op het scherm, klaar om voorgelezen en
 * gemarkeerd te worden (dyslexie, 09-10-2026).
 *
 *   <VoorleesMarkering actiefId={id} leesZin={fn} aan={bool}>
 *     <VoorleesTekst tekst="..." prefix="vraag" taal="nl" />
 *   </VoorleesMarkering>
 *
 * Elke zin wordt <span class="vl-zin" data-zin-id="vraag-0">; de zin die
 * klinkt krijgt daarbij "vl-zin-actief". Formules ($...$) blijven via
 * VraagTekst (KaTeX) staan. Met voorlezen aan is een zin klikbaar en met
 * Enter of spatie te bedienen: dan wordt die ene zin voorgelezen.
 *
 * Binnen een knop (antwoordoptie) mag geen tweede knop staan; geef daar
 * klikbaar={false} mee: de zin wordt dan alleen gemarkeerd.
 */
import React, { createContext, useContext, useMemo } from 'react';
import VraagTekst from '../VraagTekst';
import { heeftFormule } from '../../utils/formuleTekst';
import { zinnenVanBlok } from '../../utils/zinnen';
import { detecteerTaal } from '../../utils/taalDetectie';
import './VoorleesTekst.css';

/** Zin zonder formule als platte tekst; met formule via VraagTekst (KaTeX). */
function ZinInhoud({ tekst }) {
  return heeftFormule(tekst) ? <VraagTekst tekst={tekst} /> : tekst;
}

const VoorleesContext = createContext({ actiefId: null, leesZin: null, aan: false });

/**
 * @param {Object} p
 * @param {string|null} p.actiefId  id van de zin die nu klinkt
 * @param {Function|null} p.leesZin ({ id, tekst, taal }) => void
 * @param {boolean} p.aan           voorlezen staat aan (zinnen klikbaar)
 */
export function VoorleesMarkering({ actiefId = null, leesZin = null, aan = false, children }) {
  const waarde = useMemo(() => ({ actiefId, leesZin, aan }), [actiefId, leesZin, aan]);
  return <VoorleesContext.Provider value={waarde}>{children}</VoorleesContext.Provider>;
}

export function useVoorleesMarkering() {
  return useContext(VoorleesContext);
}

/**
 * Eén vast stukje tekst (zoals "Vraag 3") dat als zin met een vaste id
 * gemarkeerd kan worden, zonder te splitsen.
 */
export function VoorleesVast({ id, as: Tag = 'span', className, children, ...rest }) {
  const { actiefId } = useContext(VoorleesContext);
  const actief = actiefId != null && actiefId === id;
  return (
    <Tag className={`vl-zin${actief ? ' vl-zin-actief' : ''}${className ? ` ${className}` : ''}`} data-zin-id={id} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * @param {Object} p
 * @param {string} p.tekst
 * @param {string} p.prefix       id-voorvoegsel ('vraag', 'optie-2', 'leestekst')
 * @param {string} [p.taal]       standaardtaal voor de taaldetectie per zin
 * @param {Function} [p.onLeesZin] overschrijft leesZin uit de context
 * @param {boolean} [p.klikbaar]  false = alleen markeren (binnen knoppen)
 * @param {string} [p.as]         element ('span' | 'div' | 'p')
 */
export default function VoorleesTekst({ tekst, prefix, taal = 'nl', onLeesZin, klikbaar, as: Tag = 'span', className, style, ...rest }) {
  const ctx = useContext(VoorleesContext);
  const bron = tekst == null ? '' : String(tekst);
  const zinnen = useMemo(() => zinnenVanBlok(bron, prefix), [bron, prefix]);
  const lees = onLeesZin || ctx.leesZin;
  const isKlikbaar = klikbaar === undefined ? (ctx.aan && typeof lees === 'function') : (klikbaar && typeof lees === 'function');

  if (!bron.trim()) return null;

  const leesDeze = (zin) => {
    if (typeof lees !== 'function') return;
    lees({ id: zin.id, tekst: zin.tekst, taal: detecteerTaal(zin.tekst, taal) });
  };

  const delen = [];
  let vorigEind = 0;
  zinnen.forEach((zin) => {
    // Witruimte vóór de eerste zin niet tonen (met regels zou dat een lege regel worden).
    if (zin.start > vorigEind && (vorigEind > 0 || bron.slice(0, zin.start).trim())) delen.push(<React.Fragment key={`t-${zin.id}`}>{bron.slice(vorigEind, zin.start)}</React.Fragment>);
    const actief = ctx.actiefId != null && ctx.actiefId === zin.id;
    const klasse = `vl-zin${actief ? ' vl-zin-actief' : ''}${isKlikbaar ? ' vl-zin-klikbaar' : ''}`;
    if (isKlikbaar) {
      delen.push(
        <span
          key={zin.id}
          className={klasse}
          data-zin-id={zin.id}
          role="button"
          tabIndex={0}
          aria-pressed={actief}
          onClick={(e) => { e.stopPropagation(); leesDeze(zin); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); leesDeze(zin); }
          }}
        >
          <ZinInhoud tekst={zin.tekst} />
        </span>,
      );
    } else {
      delen.push(
        <span key={zin.id} className={klasse} data-zin-id={zin.id}>
          <ZinInhoud tekst={zin.tekst} />
        </span>,
      );
    }
    vorigEind = zin.eind;
  });
  if (vorigEind < bron.length && bron.slice(vorigEind).trim()) delen.push(<React.Fragment key="t-staart">{bron.slice(vorigEind)}</React.Fragment>);

  // Tekst met formules stond vóór het voorlezen in één element met
  // vt-met-formules (white-space: pre-wrap), zodat een meerregelige
  // wiskundevraag onder elkaar blijft staan. De regeleinden zitten nu
  // tussen de zinnen in deze buitenste Tag; dezelfde class houdt ze heel.
  const metFormules = heeftFormule(bron) ? ' vt-met-formules' : '';
  // Regels uit de vraag (opsomming "- factorijen", zinnen onder elkaar) bleven
  // zonder formule op één regel staan (15-10-2026); pre-line houdt ze heel.
  const metRegels = !metFormules && /\S[^\S\n]*\n\s*\S/.test(bron) ? ' vl-met-regels' : '';

  return (
    <Tag className={`vl-tekst${metFormules}${metRegels}${className ? ` ${className}` : ''}`} style={style} {...rest}>
      {delen}
    </Tag>
  );
}
